import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Autocomplete } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';

import { Context } from '../../context/DataContext';
import useDataGetter from '../../hooks/useDataGetter';
import SelectField from '../../components/form/SelectField';
import CheckBoxField from '../../components/form/CheckBoxField';

const ClientInfoPage = () => {
  const navigate = useNavigate();
  const { data = [] } = useDataGetter(
    `${process.env.REACT_APP_ACEMUSE_API_URL}/getTwAddress`,
    {}
  );
  const countryList = useDataGetter(
    `${process.env.REACT_APP_LEAGUE_BASE_WEB_URL}/datafile/countryList.json`,
    []
  );
  const { state, saveProfile, setCustomerInfo, checkBlacklist } =
    React.useContext(Context);
  const { customerInfo } = state;
  const [errorMsg, setErrorMsg] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [values, setValues] = React.useState({
    name: customerInfo.name || '',
    identityNumber: customerInfo.id || '',
    birthday: customerInfo.birthday || null,
    mobile: customerInfo.phone || '',
    email: customerInfo.email || '',
    city: customerInfo.city || '',
    zip_code: customerInfo.zip_code || '',
    district: customerInfo.district || '',
    address: customerInfo.restAddress || '',
    nation: customerInfo.nation || null,
  });
  const [otherNationChecked, setOtherNationChecked] = React.useState(
    customerInfo.otherNationChecked || false
  );
  const {
    name,
    identityNumber,
    birthday,
    mobile,
    email,
    city,
    district,
    zip_code,
    address,
    nation,
  } = values;

  const handleChange = (prop) => (e) =>
    setValues({ ...values, [prop]: e.target.value });

  return (
    <Box>
      <Typography
        variant='h4'
        sx={{ my: 2 }}>
        客戶資料
      </Typography>
      <Box
        component='form'
        sx={{ px: 3, pb: 8, '& > div': { my: 1 } }}
        noValidate
        autoComplete='off'>
        <TextField
          fullWidth
          id='client-name'
          label='姓名'
          value={name}
          onChange={handleChange('name')}
        />
        <TextField
          fullWidth
          id='client-id'
          label='身分證字號'
          value={identityNumber}
          onChange={handleChange('identityNumber')}
        />
        <MobileDatePicker
          label='出生年月日'
          inputFormat='yyyy-MM-dd'
          toolbarFormat='DDD'
          value={birthday}
          onChange={(value) =>
            setValues({ ...values, birthday: value.toISODate() })
          }
          renderInput={(params) => (
            <TextField
              fullWidth
              {...params}
            />
          )}
        />
        <TextField
          fullWidth
          id='client-mobile'
          label='行動電話'
          value={mobile}
          inputProps={{
            inputMode: 'numeric',
          }}
          onChange={handleChange('mobile')}
        />
        <TextField
          fullWidth
          id='client-email'
          label='信箱'
          value={email}
          onChange={handleChange('email')}
        />
        {data.length ? (
          <SelectField
            fullWidth
            id='address-city'
            label='縣市'
            placeholder='請選擇...'
            value={city}
            onChange={(e) => {
              setValues({
                ...values,
                city: e.target.value,
                district: '',
                zip_code: '',
              });
            }}
            options={data.map(({ name, id }) => ({
              id: `city_${id}`,
              value: name,
              text: name,
            }))}
          />
        ) : null}
        {data.length && city ? (
          <SelectField
            fullWidth
            id='address-district'
            label='鄉鎮市區'
            placeholder='請選擇...'
            value={!zip_code || !district ? '' : `${zip_code}_${district}`}
            onChange={(e) => {
              const [zip_code, district] = e.target.value.split('_');
              setValues({ ...values, district, zip_code });
            }}
            options={data
              .find(({ name }) => name === city)
              .districs.map(({ name, zip_code }) => ({
                id: `district_${zip_code}`,
                value: `${zip_code}_${name}`,
                text: name,
              }))}
          />
        ) : null}
        {data.length && district ? (
          <TextField
            fullWidth
            id='client-address'
            label='地址'
            value={address}
            onChange={handleChange('address')}
          />
        ) : null}
        <CheckBoxField
          fullWidth
          label='外國籍'
          value={otherNationChecked}
          onChange={(e) => setOtherNationChecked(e.target.checked)}
        />
        {otherNationChecked && countryList.length ? (
          <Autocomplete
            id='client-nation'
            options={countryList.map(({ countryName }) => countryName)}
            value={nation}
            onChange={(e, nation) => {
              setValues({ ...values, nation });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label='國籍'
              />
            )}
          />
        ) : null}
        {errorMsg ? (
          <Typography
            variant='h6'
            sx={{ my: 2, color: 'red' }}>
            {errorMsg}
          </Typography>
        ) : null}
        <LoadingButton
          variant='contained'
          sx={{ width: 168, mt: 3 }}
          loading={loading}
          onClick={() => {
            setErrorMsg('');
            if (
              !name ||
              !identityNumber ||
              !birthday ||
              !mobile ||
              !email ||
              !city ||
              !district ||
              !zip_code ||
              !address ||
              (otherNationChecked && !nation)
            ) {
              setErrorMsg('請輸入資料');
            } else {
              setLoading(true);
              checkBlacklist(
                { p1: identityNumber, p2: 'ID' },
                ({ ResponseCode, ResponseMSG }) => {
                  if (ResponseMSG === 'N' && ResponseCode === '1') {
                    const customerAddress = `${zip_code}${city}${district}${address}`;

                    saveProfile(
                      {
                        orderId: customerInfo.orderId,
                        name,
                        idCard: identityNumber,
                        birthday,
                        phone: mobile,
                        email,
                        address: customerAddress,
                        custNation: nation,
                        bank: 'bank',
                        bankAccount: '123123123123',
                      },
                      ({ responseCode, responseMsg }) => {
                        if (responseCode === '000') {
                          setCustomerInfo(
                            {
                              birthday,
                              phone: mobile,
                              address: customerAddress,
                              name,
                              id: identityNumber,
                              otherNationChecked,
                              nation,
                              email,
                              city,
                              zip_code,
                              district,
                              restAddress: address,
                            },
                            () => {
                              setLoading(false);
                              navigate('/burglary-insur/mobile-verify');
                            }
                          );
                        } else {
                          setLoading(false);
                          setErrorMsg(responseMsg);
                        }
                      }
                    );
                  } else {
                    setLoading(false);
                    setErrorMsg(ResponseMSG);
                  }
                }
              );
            }
          }}>
          下一步
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ClientInfoPage;
